import { debounce, delay } from 'lodash';
import Event from './Event';

const History = window.history || {};

// Fallback for IE<10
if (!History.pushState) {
    let parser = document.createElement('a');
    window.fakedUrl = window.location.pathname;

    History.pushState = function (state, title, url) {
        if (window.onpopstate && url !== window.location.pathname.split('?').shift()) {
            if (url.indexOf('//') !== -1) {
                parser.href = url;
                url = parser.pathname + parser.search + parser.hash;
            }

            window.fakedUrl = url;
            window.onpopstate();
        }
    };
}

let pushState = History.pushState;
History.pushState = function () {
    let result = pushState.apply(this, arguments);

    if (typeof window.onpushstate === 'function') {
        window.onpushstate();
    }

    return result;
};

/**
 * Allow to change URL and parameters instantly
 *
 * Triggers event on URL change:
 * qs:name - for search parameter `name`
 * qs - for any query string change
 * path - for path change
 * hash - for hash change
 *
 * Events send two arguments: new parameter, old parameter
 */
class Url extends Event {

    constructor() {
        super();

        let update = debounce(() => this.update(), 100);

        window.onpopstate = window.onpushstate = window.onhashchange = update;

        delay(update);
    }

    setQs(variable, value) {
        let qs;

        if (typeof variable === 'object') {
            qs = variable;
        } else {
            qs = JSON.parse(JSON.stringify(this.qs || {}));
            qs[variable] = value;
        }

        History.pushState(null, null, this.getPath() + '?' + this.encodeQueryString(qs));
    }

    setPath(path, qs) {
        if (typeof qs === 'object') {
            this.qs = qs;
            qs = void 0;
        }

        if (!qs && path.indexOf('?') === -1) {
            qs = this.encodeQueryString(this.qs);

            if (qs.length) {
                path += '?' + qs;
            }
        }

        if (window.location.pathname + window.location.search !== path) {
            History.pushState(null, null, path);
        }
    }

    setHash(hash) {
        window.location.hash = hash;
    }

    encodeQueryString(qs) {
        let values = [];

        Object.keys(qs).filter((k) => qs[k] != null).forEach((key) => {
            values.push(encodeURIComponent(key) + '=' + encodeURIComponent(qs[key]));
        });

        return values.join('&');
    }

    update() {
        let oldQs = this.qs || {};
        let oldPath = this.path;
        let oldHash = this.hash;

        this.qs = this.getQueryString();
        this.path = this.getPath();
        this.hash = this.getHash();

        if (this.path !== oldPath) {
            this.trigger('path', this.path, oldPath);
        }

        if (this.hash !== oldHash) {
            this.trigger('hash', this.hash, oldHash);
        }

        let qsDifferences = [];

        Object.keys(oldQs).concat(Object.keys(this.qs)).forEach((key) => {
            if (oldQs[key] !== this.qs[key]) {
                qsDifferences.push(key);
            }
        });

        if (qsDifferences.length) {
            this.trigger('qs', this.qs, oldQs);

            qsDifferences.forEach((key) => {
                this.trigger('qs:' + key, this.qs[key], oldQs[key]);
            });
        }
    }

    back() {
        History.back();
    }

    forward() {
        History.forward();
    }

    getPathWithQuery() {
        return window.location.pathname + window.location.search;
    }

    getPath() {
        return window.fakedUrl || window.location.pathname.split('?').shift();
    }

    decode(str) {
        return decodeURIComponent(str.replace(/\+/g, ' '));
    }

    getQueryString() {
        let search = /([^&=]+)=?([^&]*)/g,
            query = window.location.search.substring(1);

        let qs = {};

        let match;
        while (match = search.exec(query)) {
            qs[this.decode(match[1])] = this.decode(match[2]);
        }

        return qs;
    }

    getHash() {
        return window.location.hash.substr(1);
    }

    redirect(url) {
        window.location.href = url;
    }

    refresh() {
        window.location.reload();
    }
}

export default new Url();
